/**
 * The explicit Apply update confirmation (f10.3). Nothing is applied without
 * this click (BR-F10.3-6).
 *
 * `200` means the server acknowledged the update and the rail refreshes; `202`
 * means an update job was enqueued and its progress is shown with the shared
 * `JobProgress` bar (adr-1). The step stays stale until that job succeeds.
 */

import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { ApiError, type CycleWorkflowStep } from '@/api/client';
import { isJobTerminal, useApplyCycleStepUpdate, useJobProgress } from '@/api/queries';
import { queryKeys } from '@/api/queryKeys';
import { JobProgress } from '@/jobs/JobProgress';
import { Modal } from '@/routes/reference-data/Modal';

export function ApplyUpdateDialog({
  cycleId,
  step,
  onClose,
}: {
  cycleId: string;
  step: CycleWorkflowStep;
  onClose: () => void;
}) {
  const apply = useApplyCycleStepUpdate(cycleId);
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState<string | undefined>();
  const job = useJobProgress(jobId);

  useEffect(() => {
    if (job.data && isJobTerminal(job.data.state)) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.detail(cycleId) });
    }
  }, [job.data, cycleId, queryClient]);

  const submit = () => {
    if (jobId) {
      onClose();
      return;
    }
    apply.mutate(
      { stepCode: step.code, body: { expected_version: step.version } },
      {
        onSuccess: (result) => {
          if ('job_id' in result) setJobId(result.job_id);
          else onClose();
        },
      },
    );
  };

  const error = apply.error;
  const message =
    error instanceof ApiError && error.reason === 'CYCLE_STEP_VERSION_CONFLICT'
      ? 'This step changed — the latest state is shown.'
      : error instanceof Error
        ? error.message
        : null;

  return (
    <Modal
      title={`Apply ${step.name} update`}
      subtitle={`Step ${step.number}`}
      onClose={onClose}
      onSubmit={submit}
      busy={apply.isPending}
      submitLabel={jobId ? 'Close' : 'Apply update'}
      submitIcon={jobId ? undefined : <Download aria-hidden="true" />}
      narrow
      layout="plain"
      message={message}
    >
      {jobId && job.data ? (
        <JobProgress job={job.data} title={`Applying the ${step.name} update`} />
      ) : (
        <div className="alert alert-warning">
          <div>
            <div className="a-title">{step.stale?.detail ?? 'Updates are available for this step.'}</div>
            <div className="a-body">
              Applying marks the step completed again. If the update changes this step's
              data, every later completed step is flagged for review.
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
